import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AdminHeader, StatusBadge, dateBadge, sportName, timeAgo } from "./adminShared";
import { SportIcon } from "@/components/SportIcon";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { CalendarDays, CheckCircle2, Clock, FileText, Building2, Users, CreditCard, Eye, Loader2, Flag, UserPlus, Activity, Trash2, Star } from "lucide-react";

const COLORS = ["#0a1420", "#06b6d4", "#22d3ee", "#38bdf8", "#818cf8", "#f472b6"];

export default function AdminOverview() {
  const [ov, setOv] = useState(null);
  const [pending, setPending] = useState([]);
  const [activity, setActivity] = useState([]);

  const loadActivity = () => api.get("/admin/activity").then(({ data }) => setActivity(data.activity));
  useEffect(() => {
    api.get("/admin/overview").then(({ data }) => setOv(data));
    api.get("/admin/races?status=pending_approval").then(({ data }) => setPending(data.races.slice(0, 6)));
    loadActivity();
  }, []);

  const hideActivity = async (id) => {
    try { await api.delete(`/admin/activity/${id}`); loadActivity(); }
    catch { /* noop */ }
  };

  if (!ov) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={28} /></div>;

  const revCur = ov.revenue_by_currency && Object.keys(ov.revenue_by_currency).length ? ov.revenue_by_currency : null;
  const revLabel = revCur ? Object.entries(revCur).map(([c, v]) => `${c === "MYR" ? "RM" : c + " "}${v.toLocaleString()}`).join(" · ") : `RM${(ov.listing_revenue || 0).toLocaleString()}`;
  const cards = [
    { icon: CalendarDays, label: "Total Races", value: ov.total_races, to: "/admin/races" },
    { icon: CheckCircle2, label: "Published", value: ov.published_races, c: "text-emerald-600", to: "/admin/races?status=published" },
    { icon: Clock, label: "Pending Approval", value: ov.pending_approval, c: "text-amber-600", to: "/admin/submissions" },
    { icon: Star, label: "Pending Featured Requests", value: ov.pending_featured || 0, c: "text-amber-600", to: "/admin/featured" },
    { icon: FileText, label: "Drafts", value: ov.draft_races, to: "/admin/races?status=draft" },
    { icon: Building2, label: "Active Organizers", value: ov.total_organizers, to: "/admin/organizers" },
    { icon: Users, label: "Registered Runners", value: ov.total_users, to: "/admin/users" },
    { icon: CreditCard, label: "Revenue", value: revLabel, c: "text-emerald-600", to: "/admin/payments" },
    { icon: Eye, label: "Race Page Views", value: ov.total_views.toLocaleString(), c: "text-[#06b6d4]", to: "/admin/reports" },
  ];
  const pie = (ov.races_by_sport || []).map((s) => ({ name: sportName(s.sport), value: s.count }));
  const ICON = { race: Flag, payment: CreditCard, organizer: Building2, user: UserPlus };

  return (
    <div>
      <AdminHeader title="Admin Dashboard" subtitle="Your private YourRaceFinder control centre." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => { const Icon = c.icon; return (
          <Link key={i} to={c.to} data-testid={`admin-stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="block bg-white border border-slate-200 rounded-xl p-5 cursor-pointer transition hover:border-[#22d3ee] hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] active:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#22d3ee]/40">
            <Icon size={20} className="text-[#06b6d4]" />
            <div className={`font-display font-black text-2xl mt-2 ${c.c || "text-slate-800"}`}>{c.value}</div>
            <div className="text-sm text-slate-500">{c.label}</div>
          </Link>
        ); })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold uppercase text-lg text-slate-800">Pending Race Submissions</h3>
            <Link to="/admin/submissions" className="text-sm text-[#06b6d4] font-semibold hover:underline">View All</Link>
          </div>
          {pending.length === 0 ? <p className="text-slate-500 text-sm py-6 text-center">No pending submissions. All caught up! 🎉</p> : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="text-slate-400 text-xs uppercase"><tr><th className="text-left pb-2">Race</th><th className="text-left pb-2">Organizer</th><th className="text-left pb-2">Date</th><th className="text-right pb-2">Action</th></tr></thead>
              <tbody>{pending.map((r) => { const b = dateBadge(r.date); return (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="py-3 font-semibold text-slate-800 flex items-center gap-2"><SportIcon sport={r.sport} size={14} className="text-[#06b6d4]" />{r.name}</td>
                  <td className="py-3 text-slate-600">{r.organizer_name}</td>
                  <td className="py-3 text-slate-500">{b.day} {b.month} {b.year}</td>
                  <td className="py-3 text-right"><Link to={`/admin/races/${r.id}`} className="bg-[#0a1420] text-white text-xs font-bold px-3 py-1.5 rounded-lg" data-testid={`review-race-${r.id}`}>Review</Link></td>
                </tr>
              ); })}</tbody>
            </table></div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-display font-bold uppercase text-lg text-slate-800 mb-4">Races by Sport</h3>
          <div className="w-full h-40"><ResponsiveContainer><PieChart><Pie data={pie} dataKey="value" innerRadius={38} outerRadius={68} paddingAngle={2}>{pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie></PieChart></ResponsiveContainer></div>
          <div className="space-y-1 mt-2">{pie.map((d, i) => <div key={i} className="flex items-center gap-2 text-sm"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /><span className="text-slate-600">{d.name}</span><span className="ml-auto font-bold text-slate-800">{d.value}</span></div>)}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6 mt-6">
        <h3 className="font-display font-bold uppercase text-lg text-slate-800 mb-4 flex items-center gap-2"><Activity size={18} className="text-[#06b6d4]" /> Recent Activity</h3>
        <div className="space-y-3">
          {activity.map((a, i) => { const Icon = ICON[a.type] || Activity; return (
            <div key={a.id || i} className="flex items-center gap-3 text-sm group" data-testid={`activity-row-${a.id || i}`}>
              <span className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center text-slate-500 shrink-0"><Icon size={15} /></span>
              <span className="text-slate-700 font-medium">{a.text}</span>
              {a.meta && <span className="text-slate-400">· {a.meta}</span>}
              <span className="ml-auto text-slate-400 text-xs">{timeAgo(a.at)}</span>
              <button onClick={() => hideActivity(a.id)} title="Remove from activity" data-testid={`delete-activity-${a.id}`}
                className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
            </div>
          ); })}
          {activity.length === 0 && <p className="text-slate-400 text-sm">No recent activity.</p>}
        </div>
      </div>
    </div>
  );
}
